/**
 * Schlankes UI-Row-Format für RawTransfersTable (ohne raw_json / FIFO / Debug-Blobs).
 */
import { resolveNftCollectionDisplayName } from "./nftCollections.js";
import { resolveTokenAmountString } from "./formatNumbers.js";

function toUnixSecondsMaybe(x) {
  const n = Number(x);
  if (!Number.isFinite(n) || n <= 0) return 0;
  if (n > 1e12) return Math.floor(n / 1000);
  return n;
}

function pickHash(tx) {
  return (
    tx?.hash ||
    tx?.txid ||
    tx?.id ||
    tx?.transactionHash ||
    tx?.event_id ||
    ''
  );
}

/** Skip null/undefined/blank — empty string must not shadow relatedMintTokenId. */
export function pickNonEmptyNftTokenId(...values) {
  for (const value of values) {
    if (value === null || value === undefined) continue;
    const s = String(value).trim();
    if (!s || s === "—" || s === "-" || s === "#") continue;
    return s.replace(/^#/, "").trim() || null;
  }
  return null;
}

export function pickNftTokenIdFromRow(tx = {}) {
  const fromFields = pickNonEmptyNftTokenId(
    tx.tokenId,
    tx.nftTokenId,
    tx.nft_token_id,
    tx.relatedMintTokenId,
    tx.related_mint_token_id,
    // token_id last: VIP-180 rows often carry an empty token_id placeholder
    tx.token_id
  );
  if (fromFields) return fromFields;

  // Single consolidated VeMugs (60/40, batchSize=1): ID lives only on identities.
  const batchIds = [];
  if (Array.isArray(tx.nftBatchTokenIds)) {
    for (const id of tx.nftBatchTokenIds) {
      const s = pickNonEmptyNftTokenId(id);
      if (s) batchIds.push(s);
    }
  }
  if (
    !batchIds.length &&
    Array.isArray(tx.nftBatchIdentities)
  ) {
    for (const identity of tx.nftBatchIdentities) {
      const s = pickNonEmptyNftTokenId(
        identity?.tokenId,
        identity?.nft_token_id,
        identity?.token_id
      );
      if (s) batchIds.push(s);
    }
  }
  if (
    !batchIds.length &&
    Array.isArray(tx.vemugsBatchProof?.nftBatchIdentities)
  ) {
    for (const identity of tx.vemugsBatchProof.nftBatchIdentities) {
      const s = pickNonEmptyNftTokenId(
        identity?.tokenId,
        identity?.nft_token_id,
        identity?.token_id
      );
      if (s) batchIds.push(s);
    }
  }
  // Only auto-promote when exactly one NFT is represented (consolidated mint).
  if (batchIds.length === 1) return batchIds[0];

  const name = String(
    tx.nftName || tx.nft_name || tx.name || tx.relatedMintNftName || ""
  ).trim();
  const hashIdx = name.lastIndexOf("#");
  if (hashIdx >= 0) {
    const tail = name.slice(hashIdx + 1).trim();
    if (tail) return pickNonEmptyNftTokenId(tail);
  }
  return null;
}

function isBlankAmountDisplay(value) {
  if (value === null || value === undefined) {
    return true;
  }

  const s = String(value).trim();
  return !s || s === '—' || s === '-';
}

function pickAmount(tx) {
  const resolved = resolveTokenAmountString(tx);
  const n = Number(resolved);
  return Number.isFinite(n) ? n : 0;
}

const V3TAX_META_ALIASES = {
  application: ["application"],
  subApplication: ["subApplication", "sub_application"],
  merchant: ["merchant"],
  merchantWallet: ["merchantWallet", "merchant_wallet"],
  v3taxPlanId: ["v3taxPlanId", "v3tax_plan_id", "planId", "plan_id"],
  v3taxPlanName: ["v3taxPlanName", "v3tax_plan_name", "planName", "plan_name"],
  v3taxInvoiceId: ["v3taxInvoiceId", "v3tax_invoice_id", "invoiceId", "invoice_id"],
  v3taxAccessYear: ["v3taxAccessYear", "v3tax_access_year", "accessYear", "access_year"],
  v3taxFiatAmount: ["v3taxFiatAmount", "v3tax_fiat_amount", "fiatAmount", "fiat_amount"],
  v3taxFiatCurrency: ["v3taxFiatCurrency", "v3tax_fiat_currency", "fiatCurrency", "fiat_currency"],
  v3taxPaymentMatched: ["v3taxPaymentMatched", "v3tax_payment_matched"],
  v3taxPlanEstimated: ["v3taxPlanEstimated", "v3tax_plan_estimated"],
  v3taxMatchSource: ["v3taxMatchSource", "v3tax_match_source"],
};

function pickV3TaxMeta(tx = {}) {
  const out = {};
  for (const [targetKey, keys] of Object.entries(V3TAX_META_ALIASES)) {
    for (const key of keys) {
      const value = tx[key];
      if (value !== null && value !== undefined && value !== "") {
        out[targetKey] = value;
        break;
      }
    }
  }
  return out;
}

const MUGSHOT_META_ALIASES = {
  mugshotPro: ["mugshotPro", "mugshot_pro"],
  mugshotProBillingCycle: ["mugshotProBillingCycle", "mugshot_pro_billing_cycle", "billingCycle", "billing_cycle"],
  mugshotProBillingCycleEstimated: ["mugshotProBillingCycleEstimated", "mugshot_pro_billing_cycle_estimated"],
  mugshotProBillingCycleConfidence: ["mugshotProBillingCycleConfidence", "mugshot_pro_billing_cycle_confidence"],
  mugshotProBillingCycleReason: ["mugshotProBillingCycleReason", "mugshot_pro_billing_cycle_reason"],
  mugshotProFiatValue: ["mugshotProFiatValue", "mugshot_pro_fiat_value"],
  mugshotProFiatCurrency: ["mugshotProFiatCurrency", "mugshot_pro_fiat_currency"],
  mugshotProUnitPriceFiat: ["mugshotProUnitPriceFiat", "mugshot_pro_unit_price_fiat"],
  mugshotProMonthlyTargetFiat: ["mugshotProMonthlyTargetFiat", "mugshot_pro_monthly_target_fiat"],
  mugshotProYearlyTargetFiat: ["mugshotProYearlyTargetFiat", "mugshot_pro_yearly_target_fiat"],
  mugshotContract: ["mugshotContract", "mugshot_contract"],
  mugshotProReceiver: ["mugshotProReceiver", "mugshot_pro_receiver"],
  mugshotMatchedBy: ["mugshotMatchedBy", "mugshot_matched_by"],
  isMugshotProSubscription: ["isMugshotProSubscription", "is_mugshot_pro_subscription"],
};

function pickMugshotMeta(tx = {}) {
  const out = {};
  for (const [targetKey, keys] of Object.entries(MUGSHOT_META_ALIASES)) {
    for (const key of keys) {
      const value = tx[key];
      if (value !== null && value !== undefined && value !== "") {
        out[targetKey] = value;
        break;
      }
    }
  }
  return out;
}

function resolveTransferSource(tx, fallbackSource) {
  if (tx?.isSyntheticProtocolEvent || tx?.source === 'THOR_EVENT_LOG') {
    return 'THOR LOG';
  }
  return fallbackSource || tx?.source || 'VIP180';
}

const DEX_AGGREGATOR_DERIVED_COST_CLASSES = new Set([
  'dex_aggregator_execution_cost',
  // Legacy persisted analyses used this name for the same derived wallet delta.
  'dex_aggregator_fee',
]);

function normalizedClass(tx = {}) {
  return String(tx.classification || tx.type || '').trim().toLowerCase();
}

function dexAggregatorExecutionUiKey(tx = {}) {
  const hash = String(
    tx.aggregatorExecutionHash || tx.aggregatorRoundtripHash || pickHash(tx) || ''
  )
    .trim()
    .toLowerCase();
  const contract = String(
    tx.inputAssetContract ||
      tx.token_contract ||
      tx.tokenContract ||
      tx.routeAssetContract ||
      ''
  )
    .trim()
    .toLowerCase();
  const token = String(tx.token || tx.token_symbol || tx.tokenSymbol || '')
    .trim()
    .toUpperCase();
  if (!hash) return '';
  if (/^0x[a-f0-9]{40}$/.test(contract)) return `${hash}|${contract}`;
  return token ? `${hash}|${token}` : '';
}

function isDexAggregatorPhysicalInput(tx = {}) {
  const cls = normalizedClass(tx);
  return (
    cls === 'dex_aggregator_multihop_input' ||
    cls === 'dex_aggregator_same_asset_multihop_input' ||
    cls === 'dex_aggregator_roundtrip_parent'
  );
}

function isDerivedDexAggregatorCost(tx = {}) {
  if (!DEX_AGGREGATOR_DERIVED_COST_CLASSES.has(normalizedClass(tx))) return false;
  return Boolean(
    tx.receiptDerived === true ||
      tx.derivedFromWalletDelta === true ||
      tx.syntheticAggregatorLeg === true ||
      tx.source === 'RECEIPT_DERIVED'
  );
}

/**
 * Raw transfers should only count physical on-chain movements as rows. The
 * receipt-derived execution cost remains a canonical backend/FIFO event, but
 * is attached to its physical input row as non-additive display metadata.
 */
export function attachDerivedDexAggregatorCosts(rows = []) {
  if (!Array.isArray(rows) || !rows.length) return [];

  const parentsByKey = new Map();
  const costsByKey = new Map();

  for (const row of rows) {
    const key = dexAggregatorExecutionUiKey(row);
    if (!key) continue;
    if (isDexAggregatorPhysicalInput(row)) {
      parentsByKey.set(key, row);
    } else if (isDerivedDexAggregatorCost(row)) {
      costsByKey.set(key, row);
    }
  }

  const attachedCostRows = new Set();
  const enrichedParents = new Map();
  for (const [key, parent] of parentsByKey.entries()) {
    const cost = costsByKey.get(key);
    if (!cost) continue;
    const amount = Number(cost.amount_display ?? cost.amountDisplay ?? cost.amount);
    if (!(amount > 0)) continue;

    attachedCostRows.add(cost);
    enrichedParents.set(parent, {
      ...parent,
      derivedExecutionCostAmount: amount,
      derivedExecutionCostToken: String(cost.token || parent.token || '').toUpperCase(),
      derivedExecutionCostEventId: cost.event_id || cost.eventId || cost.subEventId || '',
      derivedExecutionCostReceiptVerified:
        cost.receiptVerified === true || cost.receiptDerived === true,
    });
  }

  return rows
    .filter((row) => !attachedCostRows.has(row))
    .map((row) => enrichedParents.get(row) || row);
}

/** Felder, die renderTypeCell / Filter / DEX-Heuristiken brauchen können. */
export function buildRawTransferUiRow(tx, source) {
  if (!tx || typeof tx !== 'object') return null;

  const timestamp = toUnixSecondsMaybe(tx.timestamp ?? tx.block_timestamp ?? tx.ts ?? 0);
  const token = String(tx.token || tx.token_symbol || '');
  const normalizedAmount = pickAmount(tx);
  const hash = pickHash(tx);

  return {
    hash,
    __source: resolveTransferSource(tx, source),
    token,
    token_symbol: tx.token_symbol ?? tx.tokenSymbol ?? '',
    tokenSymbol: tx.tokenSymbol ?? tx.token_symbol ?? '',
    timestamp,
    block_timestamp: tx.block_timestamp,
    ts: tx.ts,
    receiver:
      tx.receiver ||
      tx.recipient ||
      tx.to ||
      tx.to_address ||
      tx.destination ||
      '',
    sender:
      tx.sender ||
      tx.from ||
      tx.from_address ||
      tx.source ||
      tx.source_address ||
      '',
    from: tx.from || tx.from_address,
    from_address: tx.from_address,
    to: tx.to || tx.to_address,
    to_address: tx.to_address,
    recipient: tx.recipient,
    destination: tx.destination,
    source_address: tx.source_address,
    classification: tx.classification || tx.type || '',
    type: tx.classification || tx.type || '',
    amount: normalizedAmount,
    amount_display:
      isBlankAmountDisplay(tx.amount_display ?? tx.amountDisplay)
        ? normalizedAmount
        : (tx.amount_display ?? tx.amountDisplay ?? normalizedAmount),
    amountDisplay:
      isBlankAmountDisplay(tx.amountDisplay ?? tx.amount_display)
        ? normalizedAmount
        : (tx.amountDisplay ?? tx.amount_display ?? normalizedAmount),
    amount_raw: tx.amount_raw,
    nftName:
      resolveNftCollectionDisplayName(tx) ||
      tx.nftName ||
      tx.nft_name ||
      tx.name ||
      tx.relatedMintNftName ||
      tx.related_mint_nft_name ||
      '',
    nft_name:
      resolveNftCollectionDisplayName(tx) ||
      tx.nft_name ||
      tx.nftName ||
      tx.name ||
      '',
    collection_name:
      tx.collection_name ||
      tx.collectionName ||
      resolveNftCollectionDisplayName(tx) ||
      '',
    relatedMintNftName:
      tx.relatedMintNftName ||
      tx.related_mint_nft_name ||
      resolveNftCollectionDisplayName(tx) ||
      '',
    relatedMintTokenId:
      pickNftTokenIdFromRow(tx) ||
      tx.relatedMintTokenId ||
      tx.related_mint_token_id ||
      null,
    nftContract:
      tx.nftContract ||
      tx.nft_contract ||
      tx.contract ||
      tx.contractAddress ||
      null,
    tokenId: pickNftTokenIdFromRow(tx),
    nftBatchSize:
      Number(
        tx.nftBatchSize ??
          tx.vemugsBatchProof?.batchSize ??
          0
      ) || null,
    nftBatchTokenIds: Array.isArray(tx.nftBatchTokenIds)
      ? [...tx.nftBatchTokenIds]
      : Array.isArray(tx.nftBatchIdentities)
        ? tx.nftBatchIdentities
            .map((x) => x?.tokenId ?? x?.nft_token_id ?? x?.token_id)
            .filter((x) => x != null)
        : Array.isArray(tx.vemugsBatchProof?.nftBatchIdentities)
          ? tx.vemugsBatchProof.nftBatchIdentities
              .map((x) => x?.tokenId ?? x?.nft_token_id ?? x?.token_id)
              .filter((x) => x != null)
          : [],
    nftBatchIdentities: Array.isArray(tx.nftBatchIdentities)
      ? tx.nftBatchIdentities.map((x) => ({ ...x }))
      : Array.isArray(tx.vemugsBatchProof?.nftBatchIdentities)
        ? tx.vemugsBatchProof.nftBatchIdentities.map((x) => ({ ...x }))
        : [],
    nftBatchPaymentHash:
      tx.nftBatchPaymentHash ??
      tx.vemugsBatchProof?.paymentHash ??
      null,
    nftBatchMintHash:
      tx.nftBatchMintHash ??
      tx.vemugsBatchProof?.mintHash ??
      null,
    nftBatchTotalPaymentVtho:
      tx.nftBatchTotalPaymentVtho ??
      tx.vemugsBatchProof?.totalAmount ??
      null,
    nftUnitPaymentVtho:
      tx.nftUnitPaymentVtho ??
      tx.vemugsBatchProof?.unitAmount ??
      null,
    paymentMode: tx.paymentMode ?? null,
    acquisitionMatchType:
      tx.acquisitionMatchType || tx.mintMatchType || null,
    mintMatchType:
      tx.mintMatchType || tx.acquisitionMatchType || null,
    matchedNftHash: tx.matchedNftHash || null,
    flags: Array.isArray(tx.flags) ? tx.flags : undefined,
    exchange_source: tx.exchange_source,
    exchange: tx.exchange,
    internal_flag: tx.internal_flag,
    refund: tx.refund,
    refundOfHash: tx.refundOfHash,
    refund_of_hash: tx.refund_of_hash,
    pool: tx.pool,
    pair: tx.pair,
    pairAddress: tx.pairAddress,
    poolAddress: tx.poolAddress,
    liquidityPairAddress: tx.liquidityPairAddress || tx.liquidity_pair_address,
    liquidity_pair_address: tx.liquidity_pair_address || tx.liquidityPairAddress,
    liquidity_pool: tx.liquidity_pool,
    pair_contract: tx.pair_contract,
    contractAddress: tx.contractAddress,
    contract: tx.contract,
    token_contract: tx.token_contract,
    tokenContract: tx.tokenContract || tx.token_contract,
    liquidityDex: tx.liquidityDex,
    liquidityDexKey: tx.liquidityDexKey,
    liquidityDexLabel: tx.liquidityDexLabel,
    liquidityDexIcon: tx.liquidityDexIcon,
    dexKey: tx.dexKey,
    dex: tx.dex,
    dexLabel: tx.dexLabel,
    dexIcon: tx.dexIcon,
    protocol: tx.protocol,
    protocolLabel: tx.protocolLabel,
    protocolIcon: tx.protocolIcon,
    meta: tx.meta && typeof tx.meta === 'object'
      ? { poolAddress: tx.meta.poolAddress }
      : undefined,
    symbol: tx.symbol,
    asset: tx.asset,
    source: tx.source,
    isSyntheticProtocolEvent: tx.isSyntheticProtocolEvent === true,
    syntheticAggregatorLeg: tx.syntheticAggregatorLeg === true,
    receiptDerived: tx.receiptDerived === true,
    derivedFromWalletDelta: tx.derivedFromWalletDelta === true,
    derivedSource: tx.derivedSource,
    aggregatorExecution: tx.aggregatorExecution === true,
    aggregatorExecutionHash: tx.aggregatorExecutionHash || tx.aggregatorRoundtripHash,
    aggregatorPhysicalLegRole: tx.aggregatorPhysicalLegRole,
    sameAssetNetQuantityDelta: tx.sameAssetNetQuantityDelta,
    sameAssetNetQuantityPositive: tx.sameAssetNetQuantityPositive === true,
    executionSemanticType: tx.executionSemanticType,
    executionType: tx.executionType,
    executionHopIndex: tx.executionHopIndex,
    executionHopCount: tx.executionHopCount,
    // Legacy persisted analyses
    aggregatorRoundtrip: tx.aggregatorRoundtrip === true,
    aggregatorRoundtripHash: tx.aggregatorRoundtripHash,
    routeAssetContract: tx.routeAssetContract,
    routeVenueLabels: Array.isArray(tx.routeVenueLabels) ? tx.routeVenueLabels : undefined,
    routeVenueLabel: tx.routeVenueLabel,
    executionRouteLabel: tx.executionRouteLabel,
    netExecutionCostAmount: tx.netExecutionCostAmount,
    executionCostAmount: tx.executionCostAmount,
    inputNotRoutedAmount: tx.inputNotRoutedAmount,
    routeExecutionLossAmount: tx.routeExecutionLossAmount,
    ...pickV3TaxMeta(tx),
    ...pickMugshotMeta(tx),
    needsReview: tx.needsReview,
    reviewReason: tx.reviewReason,
    token_name: tx.token_name ?? tx.tokenName,
    tokenName: tx.tokenName ?? tx.token_name,

    // Stable override / multi-event identity (must match backend txKey)
    clause_index:
      tx.clause_index ??
      tx.clauseIndex ??
      tx.raw_json?.clause_index ??
      tx.raw_json?.clauseIndex ??
      tx.raw_json?.clause ??
      null,
    clauseIndex:
      tx.clauseIndex ??
      tx.clause_index ??
      tx.raw_json?.clauseIndex ??
      tx.raw_json?.clause_index ??
      tx.raw_json?.clause ??
      null,
    event_index: tx.event_index ?? tx.eventIndex ?? tx.raw_json?.event_index,
    eventIndex: tx.eventIndex ?? tx.event_index ?? tx.raw_json?.eventIndex,
    log_index: tx.log_index ?? tx.logIndex ?? tx.raw_json?.log_index,
    logIndex: tx.logIndex ?? tx.log_index ?? tx.raw_json?.logIndex,
    subEventId: tx.subEventId ?? tx.sub_event_id,
    sub_event_id: tx.sub_event_id ?? tx.subEventId,
    event_id: tx.event_id ?? tx.eventId,
    eventId: tx.eventId ?? tx.event_id,
    idx: tx.idx,
    manualOverrideKey: tx.manualOverrideKey,
    manualClassification: tx.manualClassification,
    isManualOverride: tx.isManualOverride,
    manualOverride: tx.manualOverride,
    originalClassification: tx.originalClassification,

    // Protocol/application semantics required for branded economic-flow rows.
    pycReceiptVerified: tx.pycReceiptVerified === true,
    receiptVerified: tx.receiptVerified === true,
    pycAppId: tx.pycAppId,
    pycExchange: tx.pycExchange,
    linkedPurchaseHash: tx.linkedPurchaseHash,
    protocolClassification: tx.protocolClassification,
    economicClassification: tx.economicClassification,
    economicEventType: tx.economicEventType,
    application: tx.application,
    subApplication: tx.subApplication,
    incomeSource: tx.incomeSource,
    navigatorRegistry: tx.navigatorRegistry,
    navigatorAddress: tx.navigatorAddress,
    navigatorRoundId: tx.navigatorRoundId,
    navigatorFeeSource: tx.navigatorFeeSource,
    cashbackProgram: tx.cashbackProgram,
    cashbackEvidence: tx.cashbackEvidence,
    x2earnAppId: tx.x2earnAppId,
    x2earnAppSlug: tx.x2earnAppSlug,
    x2earnRewardKind: tx.x2earnRewardKind,
    greencartRewardPath: tx.greencartRewardPath,
    greencartRewardEvidence: tx.greencartRewardEvidence,
    groncardGiftCardRewardConfirmed: tx.groncardGiftCardRewardConfirmed,
    groncardGiftCardPurchaseConfirmed: tx.groncardGiftCardPurchaseConfirmed,
    groncardGiftCardPaymentAddress: tx.groncardGiftCardPaymentAddress,
    groncardGiftCardDistributor: tx.groncardGiftCardDistributor,
    groncardGiftCardSweepHash: tx.groncardGiftCardSweepHash,
    groncardGiftCardPurchaseAmountRaw: tx.groncardGiftCardPurchaseAmountRaw,
    groncardGiftCardRewardHash: tx.groncardGiftCardRewardHash,
    groncardGiftCardLinkMode: tx.groncardGiftCardLinkMode,
    trigger: tx.trigger,
    triggerType: tx.triggerType,
    triggerChain: tx.triggerChain,
    linkedOnchainPaymentObserved: tx.linkedOnchainPaymentObserved,
    linkedPaymentRequiredOnVeChain: tx.linkedPaymentRequiredOnVeChain,
    evidenceLevel: tx.evidenceLevel,
    providerSemanticType: tx.providerSemanticType,
    providerSuggestedLabel: tx.providerSuggestedLabel,
    sourcePlatform: tx.sourcePlatform,
    vaiyansVcoPrediction: tx.vaiyansVcoPrediction === true,
    vaiyansVcoPredictionLegRole: tx.vaiyansVcoPredictionLegRole,
    vaiyansVcoPredictionGroupId: tx.vaiyansVcoPredictionGroupId,
    economicGroupId: tx.economicGroupId,
    vaiyansVcoPredictionReceiver: tx.vaiyansVcoPredictionReceiver,
    vaiyansVcoPredictionBurnAddress: tx.vaiyansVcoPredictionBurnAddress,
    transactionPatternVerified: tx.transactionPatternVerified === true,
    pairedTransferVerified: tx.pairedTransferVerified === true,
    taxTreatment: tx.taxTreatment,
    deTaxTreatment: tx.deTaxTreatment,
    excludeFromTaxIncome: tx.excludeFromTaxIncome === true,
    taxable: tx.taxable,
    airdropProgram: tx.airdropProgram,
    airdropBasis: tx.airdropBasis,
    distributionMode: tx.distributionMode,
    mintedFromZeroAddress: tx.mintedFromZeroAddress === true,
    partOfVetDomains: tx.partOfVetDomains === true,
    domainName: tx.domainName || tx.domainLabel || "",
    domainLabel: tx.domainLabel || "",
    domainOwner: tx.domainOwner || "",
    swap_pair_id: tx.swap_pair_id ?? tx.swapPairId ?? null,
    swapPairId: tx.swapPairId ?? tx.swap_pair_id ?? null,
    swap_group_id: tx.swap_group_id ?? tx.swapGroupId ?? null,
    swapGroupId: tx.swapGroupId ?? tx.swap_group_id ?? null,
    swap_role: tx.swap_role ?? tx.swapRole ?? null,
    swapRole: tx.swapRole ?? tx.swap_role ?? null,
    swap_counter_asset:
      tx.swap_counter_asset ?? tx.swapCounterAsset ?? null,
    swapCounterAsset:
      tx.swapCounterAsset ?? tx.swap_counter_asset ?? null,
    swap_counter_amount:
      tx.swap_counter_amount ?? tx.swapCounterAmount ?? null,
    swapCounterAmount:
      tx.swapCounterAmount ?? tx.swap_counter_amount ?? null,
  };
}

export function attachNftTokenIdsAcrossSameHash(rows = []) {
  if (!Array.isArray(rows) || rows.length < 2) return rows;

  const byHash = new Map();
  for (const row of rows) {
    const hash = String(row?.hash || "").toLowerCase();
    if (!hash || hash === "unknown") continue;
    if (!byHash.has(hash)) byHash.set(hash, []);
    byHash.get(hash).push(row);
  }

  for (const group of byHash.values()) {
    const donor = group.find((row) => pickNftTokenIdFromRow(row));
    if (!donor) continue;

    const tokenId = pickNftTokenIdFromRow(donor);
    const nftName =
      resolveNftCollectionDisplayName(donor) ||
      donor.nftName ||
      donor.nft_name ||
      donor.name ||
      null;
    const nftContract =
      donor.nftContract ||
      donor.nft_contract ||
      donor.contract ||
      donor.contractAddress ||
      null;

    for (const row of group) {
      const cls = String(row.classification || row.type || "")
        .trim()
        .toLowerCase();
      const isNftFacing =
        cls === "nft_mint" ||
        cls === "mint_payment" ||
        cls === "nft_marketplace_purchase_payment" ||
        cls === "vemugs_mint_zero_transfer" ||
        cls === "sale_payment";
      if (!isNftFacing) continue;
      if (pickNftTokenIdFromRow(row)) continue;

      row.tokenId = tokenId;
      row.nft_token_id = tokenId;
      row.relatedMintTokenId = tokenId;
      row.related_mint_token_id = tokenId;
      if (nftName && !row.nftName) {
        row.nftName = nftName;
        row.nft_name = nftName;
      }
      if (nftContract && !row.nftContract) {
        row.nftContract = nftContract;
        row.nft_contract = nftContract;
      }
    }
  }

  return rows;
}

export function mapRawTransfersForTable(rows, source) {
  if (!Array.isArray(rows) || !rows.length) return [];
  const out = [];
  for (let i = 0; i < rows.length; i++) {
    const row = buildRawTransferUiRow(rows[i], source);
    if (row) out.push(row);
  }
  // Keep VeMugs 60/40 mint legs as separate rows (same as Thorheads splits).
  // Propagate tokenId onto payment legs that lost it to empty token_id aliases.
  return attachDerivedDexAggregatorCosts(attachNftTokenIdsAcrossSameHash(out));
}

export function filterRawTransfersByYear(rows, yearStr) {
  if (!yearStr || !/^\d{4}$/.test(String(yearStr))) return rows;
  const y = String(yearStr);
  return rows.filter((tx) => {
    const ts = Number(tx?.timestamp || 0);
    if (!ts) return false;
    return new Date(ts * 1000).getFullYear().toString() === y;
  });
}
